import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useState } from "react";

const features = [
  "Unlimited data usage",
  "No throttling during peak hours",
  "24/7 customer support",
  "No hidden charges",
  "Refund policy available",
  "Free installation (annual plans)",
];

const plans = [
  {
    name: "Basic 50",
    speed: "50 Mbps",
    monthly: "₹550/-",
    annual: "₹5,500/-",
    savings: "Save ₹11,100",
    bestFor: "Light browsing, emails, social media",
  },
  {
    name: "Standard 80",
    speed: "80 Mbps",
    monthly: "₹600/-",
    annual: "₹6,000/-",
    savings: "Save ₹11,200",
    bestFor: "Streaming, video calls, small family",
  },
  {
    name: "Premium 100",
    speed: "100 Mbps",
    monthly: "₹650/-",
    annual: "₹6,500/-",
    savings: "Save ₹11,300",
    bestFor: "HD streaming, gaming, work from home",
    popular: true,
  },
  {
    name: "Ultra 150",
    speed: "150 Mbps",
    monthly: "₹750/-",
    annual: "₹7,500/-",
    savings: "Save ₹11,500",
    bestFor: "4K streaming, multiple devices, gaming",
  },
];

export default function Plans() {
  const [cycle, setCycle] = useState("monthly");
  return (
    <section id="plans" className="py-12 sm:py-16">
      <div className="container-max">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold">Choose Your Perfect Plan</h2>
          <p className="text-brand-blue/70 mt-1 sm:mt-2">No Internet No Cost — Unlimited Usage</p>
        </div>
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex rounded-lg border bg-white p-1 text-sm">
            <button
              className={`px-3 sm:px-4 py-2 rounded-md text-sm sm:text-base ${cycle === 'monthly' ? 'bg-[color:var(--brand-orange)] text-white' : 'text-brand-blue/80'}`}
              onClick={() => setCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-3 sm:px-4 py-2 rounded-md text-sm sm:text-base ${cycle === 'annual' ? 'bg-[color:var(--brand-orange)] text-white' : 'text-brand-blue/80'}`}
              onClick={() => setCycle('annual')}
            >
              Annual
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {plans.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className={`card p-4 sm:p-6 flex flex-col ${
                p.popular ? "ring-2 ring-[color:var(--brand-orange)]" : ""
              }`}
            >
              {p.popular && (
                <div className="mb-2 inline-flex items-center rounded-full bg-[color:var(--brand-orange)]/10 text-[color:var(--brand-orange)] px-3 py-1 text-xs font-semibold self-start">
                  MOST POPULAR
                </div>
              )}
              <div className="text-sm text-brand-blue/70">UL — Unlimited</div>
              <div className="mt-1 text-2xl font-bold">{p.speed}</div>
              <div className="mt-1 text-lg font-semibold">{p.name}</div>
              {cycle === 'monthly' ? (
                <div className="mt-4">
                  <div className="text-sm text-brand-blue/70">1 Month</div>
                  <div className="text-xl font-bold">{p.monthly}</div>
                </div>
              ) : (
                <div className="mt-4">
                  <div className="text-sm text-brand-blue/70">12 Month</div>
                  <div className="text-xl font-bold">{p.annual}</div>
                  <div className="text-xs font-medium text-green-600 mt-1">{p.savings}</div>
                </div>
              )}
              <div className="mt-3 text-sm text-brand-blue/70">Best for: {p.bestFor}</div>
              <ul className="mt-3 sm:mt-4 space-y-1.5 text-sm text-brand-blue/80">
                {features.map((f) => (
                  <li key={f} className="flex gap-2 items-start">
                    <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 sm:mt-6">
                <a href="#contact" className={`btn w-full text-sm ${p.popular ? "btn-primary" : "btn-outline"}`}>
                  Choose Plan
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="card mt-6 sm:mt-8 p-4 sm:p-6 border-2 border-[color:var(--brand-orange)] text-center"
        >
          <div className="text-sm text-brand-blue/70">Special 3-Year Offer</div>
          <div className="text-xl sm:text-2xl font-extrabold mt-1">2 Year Premium Plan + 1 Year FREE</div>
          <div className="text-sm text-brand-blue/70 mt-1">Total 3 Years Service</div>
          <a href="#contact" className="btn btn-primary mt-3 sm:mt-4 text-sm sm:text-base">Book Now — Limited Time!</a>
        </motion.div>
      </div>
    </section>
  );
}
